import { Languages, LogOut, User } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback } from "../avatar";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../dropdown-menu";

import { useTheme } from "@/components/theme-provider-context";
import { availableLanguages } from "@/config/languages";
import { getFullNameAndAvatarText } from "@/lib/helpers/authHelpers";
import { useAuth } from "@/lib/hooks/useAuth";
import { useLanguageSelector } from "@/lib/hooks/useLanguageSelector";
import useToast from "@/lib/hooks/useToast";
import { truncateEmail } from "@/lib/utils/truncateEmail";
import { getTheme, isThemeId, THEMES } from "@/themes/registry";

const UserMenu = () => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { avatarText } = getFullNameAndAvatarText();
  const email = localStorage.getItem("email") || "";
  const displayEmail = useMemo(() => truncateEmail(email), [email]);
  const { showToast } = useToast();
  const { theme, setTheme } = useTheme();
  const activeTheme = getTheme(theme);
  const ActiveThemeIcon = activeTheme.icon;
  const { currentLanguage, changeLanguage } = useLanguageSelector();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="rounded-full p-0.5">
          <Avatar className="size-8">
            <AvatarFallback>{avatarText}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          aria-readonly
          onClick={() => {
            navigator.clipboard.writeText(email);
            showToast(
              t("profileDropdown.emailCopied") || "Email copied to clipboard",
            );
          }}
        >
          <span className="inline-flex gap-2 items-center" title={email}>
            <User />
            {displayEmail}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ActiveThemeIcon />
            <span className="flex-1">{t("profileDropdown.theme", "Theme")}</span>
            <span className="text-xs text-muted-foreground">{activeTheme.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => {
                if (isThemeId(value)) setTheme(value);
              }}
            >
              {THEMES.map(({ id, label, icon: Icon }) => (
                <DropdownMenuRadioItem key={id} value={id}>
                  <Icon />
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages />
            <span className="flex-1">{t("profileDropdown.language", "Language")}</span>
            <span className="text-xs text-muted-foreground">
              {currentLanguage.code.toUpperCase()}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={currentLanguage.code} onValueChange={changeLanguage}>
              {availableLanguages.map((language) => (
                <DropdownMenuRadioItem key={language.code} value={language.code}>
                  {language.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          <span className="inline-flex gap-2 items-center">
            <LogOut />
            {t("common.signOut") || "Sign Out"}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
